import React, { useState, useEffect } from "react";
import Select from "react-select";
import clsx from "clsx";
import UploadBox from "./UploadBox";
import { InstalltionFormSteps } from "../../../providers/InstallationFormProvider";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import SimpleMobileStepper from "../../Stepper/Stepper";
import { Tooltip } from "react-tooltip";
import toast from "react-hot-toast";
import { getSignedUrl } from "../../../models/_publicCall";

interface MachineDataProps {
  machinesDB: any[];
  setCurrentStep: (step: number) => void;
  setSelectedMachineForTicket: (machine: any) => void;
  storedData: any;
  jobStatus: string;
  installationJobDB: any;
  blendersDB: any[];
  tubeShieldsDB: any[];
  ledRingsDB: any[];
  cupHolderDB: any[];
  boardsDB: any[];
  installationJobStepsRequired: { id: number; label: string }[];
  refetchBlenders: () => void;
  refetchBoards: () => void;
  refetchTubeShields: () => void;
  refetchCupHolders: () => void;
  refetchLedRings: () => void;
}

interface MachineDataEntry {
  machineId: string;
  smoothiesMade: boolean;
  sanitizerPassed: boolean;
  statusCheckPassed: boolean;
  statusCheckImage?: any[];
  waterCalibrated: boolean;
  isPolyfuses: boolean;
  filterType: string;
  blenderId: string | null;
  tubeShieldId: string | null;
  cupHolderId: string | null;
  ledRingId: string | null;
  boardId: string | null;
  notes: string;
}

export const filterOptions = [
  { value: "no_filter", label: "No Filter" },
  { value: "smoodi_filter", label: "Smoodi Provided Filter" },
  { value: "store_filter", label: "Store's Filter" },
];

export const MachineData: React.FC<MachineDataProps> = ({
  machinesDB,
  setCurrentStep,
  setSelectedMachineForTicket,
  storedData,
  jobStatus,
  installationJobDB,
  blendersDB,
  ledRingsDB,
  tubeShieldsDB,
  cupHolderDB,
  boardsDB,
  installationJobStepsRequired,
  refetchBlenders,
  refetchBoards,
  refetchTubeShields,
  refetchCupHolders,
  refetchLedRings,
}) => {
  const [machineDataEntries, setMachineDataEntries] = useState<
    MachineDataEntry[]
  >([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState<any[]>([]);
  const [resultImages, setResultImages] = useState<any[]>([]);
  const [validationErrors, setValidationErrors] = useState<{
    statusCheckImage?: string;
    cupHolderId?: string;
    tubeShieldId?: string;
    blenderId?: string;
    boardId?: string;
    ledRingId?: string;
  }>({});

  const getBase64 = (file: any) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
    });

  function deleteHandler(image: any) {
    setSelectedImages(selectedImages.filter((e: any) => e.url !== image.url));
    setResultImages(resultImages.filter((e: any) => e.url !== image.url));
    URL.revokeObjectURL(image.url);
  }

  type OptionType = {
    value: string;
    label: string;
    media_url: string | null;
    description: string | null;
  };
  const [blenderOptions, setBlenderOptions] = useState<OptionType[]>([]);
  const [tubeShieldOptions, setTubeShieldOptions] = useState<OptionType[]>([]);
  const [cupHolderOptions, setCupHolderOptions] = useState<OptionType[]>([]);
  const [boardOptions, setBoardOptions] = useState<OptionType[]>([]);
  const [ledRingOptions, setLedRingOptions] = useState<OptionType[]>([]);

  const getSignedUrlAndOpenIt = async (fileKey: string) => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_FETCH_API_URL}/api/media/${fileKey}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      let data;
      try {
        data = await result.json();
      } catch (jsonError) {
        console.error("[DEBUG] result.json() failed:", jsonError);
        const text = await result.text();
        console.error("[DEBUG] Response text:", text);
        throw jsonError;
      }
      if (data?.data?.url) {
        window.open(data.data.url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error getting signed URL:", error);
    }
  };

  // Keep dropdown options in sync with DB arrays
  useEffect(() => {
    setBoardOptions(
      boardsDB.map((b) => ({
        value: b.id,
        label: b.title,
        media_url: b.media_url,
        description: b.description,
      })),
    );
  }, [boardsDB]);

  useEffect(() => {
    setBlenderOptions(
      blendersDB.map((b) => ({
        value: b.id,
        label: b.title,
        media_url: b.media_url,
        description: b.description,
      })),
    );
  }, [blendersDB]);

  useEffect(() => {
    setTubeShieldOptions(
      tubeShieldsDB.map((t) => ({
        value: t.id,
        label: t.name,
        media_url: t.media_url,
        description: t.description,
      })),
    );
  }, [tubeShieldsDB]);

  useEffect(() => {
    setCupHolderOptions(
      cupHolderDB.map((c) => ({
        value: c.id,
        label: c.title,
        media_url: c.media_url,
        description: c.description,
      })),
    );
  }, [cupHolderDB]);

  useEffect(() => {
    setLedRingOptions(
      ledRingsDB.map((l) => ({
        value: l.id,
        label: l.name,
        media_url: l.media_url,
        description: l.description,
      })),
    );
  }, [ledRingsDB]);

  // ----------------- LOAD DATA - prioritize localStorage -----------------
  useEffect(() => {
    const savedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );

    // Initialize indexes
    savedData.currentMachineIndex = savedData.currentMachineIndex || 0;
    savedData.returningFromTicket = false;

    const hasMachineDataFields =
      Array.isArray(savedData.machineSetup) &&
      savedData.machineSetup.length > 0 &&
      savedData.machineSetup.some(
        (entry: any) =>
          entry.smoothiesMade !== undefined ||
          entry.sanitizerPassed !== undefined ||
          entry.statusCheckPassed !== undefined ||
          entry.waterCalibrated !== undefined ||
          entry.filterType !== undefined ||
          entry.notes !== undefined ||
          entry.isPolyfuses !== undefined ||
          entry.blenderId !== undefined ||
          entry.tubeShieldId !== undefined ||
          entry.cupHolderId !== undefined ||
          entry.ledRingId !== undefined ||
          entry.boardId !== undefined,
      );

    if (hasMachineDataFields) {
      const entries = savedData.machineSetup.map((entry: any) => ({
        machineId: entry.selectedMachine?.id || entry.machineId || "",
        smoothiesMade:
          entry.smoothiesMade !== undefined ? entry.smoothiesMade : false,
        sanitizerPassed:
          entry.sanitizerPassed !== undefined ? entry.sanitizerPassed : false,
        statusCheckPassed:
          entry.statusCheckPassed !== undefined
            ? entry.statusCheckPassed
            : false,
        isPolyfuses:
          entry.isPolyfuses !== undefined ? entry.isPolyfuses : false,
        statusCheckImage: entry.statusCheckImage || [],
        waterCalibrated:
          entry.waterCalibrated !== undefined ? entry.waterCalibrated : false,
        filterType: entry.filterType || "no_filter",
        blenderId: entry.blenderId || null,
        tubeShieldId: entry.tubeShieldId || null,
        cupHolderId: entry.cupHolderId || null,
        notes: entry.notes || "",
      }));

      setMachineDataEntries(entries);
      const currentIndex = savedData.currentMachineIndex || 0;
      setCurrentIndex(currentIndex);
      setSelectedImages(entries[currentIndex]?.statusCheckImage || []);
      setResultImages(entries[currentIndex]?.statusCheckImage || []);
      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify(savedData),
      );
      return; // Don't override with DB data if user has made changes
    }

    if (jobStatus !== "Completed" && installationJobDB?.machines?.length > 0) {
      const restoredEntries = installationJobDB.machines.map((m: any) => ({
        serialNumber: m.serialNumber,
        currentMachineName: m.currentMachineName,
        newMachineName: m.newMachineName,
        deploymentDate: m.deploymentDate,
        machineId: m.id || "",
        smoothiesMade: !!m.made4Smoothies,
        sanitizerPassed: !!m.passedSanitizerTest,
        statusCheckPassed: !!m.getStatusPassed,
        isPolyfuses: !!m.isPolyfuses,
        statusCheckImage: (() => {
          const source =
            m.uploadImagesOrVideos?.getStatusCheckImage ??
            installationJobDB.uploadImagesOrVideos?.getStatusCheckImage ??
            [];
          if (!Array.isArray(source) || source.length === 0) return [];
          if (
            source[0] &&
            typeof source[0] === "object" &&
            (source[0] as any).machine_assets
          ) {
            const currentMachineId = m.id || m.machineId;
            const group = (source as any[]).find(
              (g: any) => g?.machine_id === currentMachineId,
            );
            return group?.machine_assets || [];
          }
          // Old format: [ [ ...files ], [ ...files ] ]
          return (source as any[]).flat();
        })(),
        waterCalibrated: !!m.waterLevelCalibratedWithScale,
        filterType: m.filterType || "no_filter",
        blenderId: m.blenderId || null,
        tubeShieldId: m.tubeShieldId || null,
        cupHolderId: m.cupHolderId || null,
        notes: m.machineNotes || "",
      }));

      if (restoredEntries.length > 0) {
        setMachineDataEntries(restoredEntries);

        const currentIndex = savedData.currentMachineIndex || 0;
        setCurrentIndex(currentIndex);
        const currentImages =
          restoredEntries[currentIndex]?.statusCheckImage || [];
        setSelectedImages(currentImages);
        setResultImages(currentImages);

        // Merge with existing machineSetup to preserve machine selection, deployment info, and newMachineName
        savedData.machineSetup = savedData.machineSetup || [];
        savedData.machineSetup = restoredEntries.map(
          (entry: any, idx: number) => {
            const existing = savedData.machineSetup[idx] || {};
            return {
              ...existing, // Preserve machine selection, deploymentDate, newMachineName from MachineSetup
              ...entry, // Overwrite with MachineData fields from DB
              machineId: entry.machineId,
              selectedMachine:
                existing.selectedMachine ||
                machinesDB.find((m) => m.id === entry.machineId),
              selectedMachineId: existing.selectedMachineId || entry.machineId,
            };
          },
        );

        savedData.currentMachineIndex = savedData.currentMachineIndex || 0;
        savedData.returningFromTicket = false;

        localStorage.setItem(
          INSTALLATION_JOB_STORAGE_KEY,
          JSON.stringify(savedData),
        );
      }
    } else if (
      Array.isArray(savedData.machineSetup) &&
      savedData.machineSetup.length > 0
    ) {
      // If no DB data but we have machineSetup from MachineSetup step, initialize MachineData entries
      const entries = savedData.machineSetup.map((entry: any) => ({
        machineId:
          entry.selectedMachine?.id ||
          entry.machineId ||
          entry.selectedMachineId ||
          "",
        smoothiesMade:
          entry.smoothiesMade !== undefined ? entry.smoothiesMade : false,
        sanitizerPassed:
          entry.sanitizerPassed !== undefined ? entry.sanitizerPassed : false,
        statusCheckPassed:
          entry.statusCheckPassed !== undefined
            ? entry.statusCheckPassed
            : false,
        isPolyfuses:
          entry.isPolyfuses !== undefined ? entry.isPolyfuses : false,
        statusCheckImage: entry.statusCheckImage || [],
        waterCalibrated:
          entry.waterCalibrated !== undefined ? entry.waterCalibrated : false,
        filterType: entry.filterType || "no_filter",
        blenderId: entry.blenderId || null,
        tubeShieldId: entry.tubeShieldId || null,
        cupHolderId: entry.cupHolderId || null,
        notes: entry.notes || "",
        serialNumber: entry.serialNumber,
        currentMachineName: entry.currentMachineName,
        newMachineName: entry.newMachineName,
        deploymentDate: entry.deploymentDate,
      }));

      setMachineDataEntries(entries);
      const currentIndex = savedData.currentMachineIndex || 0;
      setCurrentIndex(currentIndex);
      setSelectedImages(entries[currentIndex]?.statusCheckImage || []);
      setResultImages(entries[currentIndex]?.statusCheckImage || []);
    }
  }, [jobStatus, installationJobDB, machinesDB]);

  // ----------------- SAVE CURRENT MACHINE -----------------
  const saveCurrentMachine = () => {
    const stored = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );
    if (!stored.machineSetup) stored.machineSetup = [];
    const statusCheckImages = resultImages.map((img) => ({
      name: img.name,
      type: img.type,
      url: img.url || img.content, // Handle both url and content formats
    }));

    stored.machineSetup[currentIndex] = {
      ...stored.machineSetup[currentIndex], // Preserve original fields like newMachineName and deploymentDate
      machineId: machineDataEntries[currentIndex].machineId,
      smoothiesMade: machineDataEntries[currentIndex].smoothiesMade,
      sanitizerPassed: machineDataEntries[currentIndex].sanitizerPassed,
      statusCheckPassed: machineDataEntries[currentIndex].statusCheckPassed,
      isPolyfuses: machineDataEntries[currentIndex].isPolyfuses,
      statusCheckImage: statusCheckImages,
      waterCalibrated: machineDataEntries[currentIndex].waterCalibrated,
      filterType: machineDataEntries[currentIndex].filterType,
      blenderId: machineDataEntries[currentIndex].blenderId,
      tubeShieldId: machineDataEntries[currentIndex].tubeShieldId,
      cupHolderId: machineDataEntries[currentIndex].cupHolderId,
      notes: machineDataEntries[currentIndex].notes,
    };

    try {
      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify(stored),
      );
    } catch (error) {
      console.error("Failed to save data to localStorage:", error);
    }
  };

  const validateBeforeNext = (): boolean => {
    const current = machineDataEntries[currentIndex];
    const errors: {
      statusCheckImage?: string;
      cupHolderId?: string;
      tubeShieldId?: string;
      blenderId?: string;
    } = {};
    if (
      current.statusCheckPassed &&
      (!resultImages || resultImages.length === 0)
    ) {
      errors.statusCheckImage =
        "Please upload at least one image or video for 'Get Status' check.";
    }

    if (
      !current.cupHolderId ||
      !cupHolderDB.find((c) => c.id === current.cupHolderId)
    ) {
      errors.cupHolderId = "Please select cupHolder.";
    }

    if (
      !current.tubeShieldId ||
      !tubeShieldsDB.find((t) => t.id === current.tubeShieldId)
    ) {
      errors.tubeShieldId = "Please select tubeShield.";
    }

    if (
      !current.blenderId ||
      !blendersDB.find((b) => b.id === current.blenderId)
    ) {
      errors.blenderId = "Please select blender.";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      toast.error(
        Object.keys(validationErrors)
          .map((key) => validationErrors[key as keyof typeof validationErrors])
          .join("\n"),
      );
    }
  }, [validationErrors]);

  const handleNext = () => {
    saveCurrentMachine();
    if (!validateBeforeNext()) return;

    setSelectedMachineForTicket(
      machinesDB.find(
        (m) => m.id === machineDataEntries[currentIndex].machineId,
      ),
    );

    if (currentIndex < machineDataEntries.length - 1) {
      setCurrentIndex(currentIndex + 1);
      const stored = JSON.parse(
        localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
      );
      stored.currentMachineIndex = currentIndex + 1;
      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify(stored),
      );

      const nextImages =
        stored.machineSetup[currentIndex + 1]?.statusCheckImage || [];
      setSelectedImages(nextImages);
      setResultImages(nextImages);
      setValidationErrors({});
    } else {
      setCurrentStep(InstalltionFormSteps.LOCATION_INFORMATION);
    }
  };

  const handleSkip = () => {
    saveCurrentMachine();
    if (!validateBeforeNext()) return;
    setCurrentStep(InstalltionFormSteps.LOCATION_INFORMATION);
  };

  const handleBack = () => {
    saveCurrentMachine();

    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      const stored = JSON.parse(
        localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
      );
      const prevImages =
        stored.machineSetup[currentIndex - 1]?.statusCheckImage || [];
      setSelectedImages(prevImages);
      setResultImages(prevImages);
      setValidationErrors({});
    } else {
      setCurrentStep(InstalltionFormSteps.MACHINE_SETUP);
    }
  };

  const entry = machineDataEntries[currentIndex];
  const machine = machinesDB.find((m) => m.id === entry?.machineId);

  if (!entry) return <p>No machine data found.</p>;

  type BooleanFieldKeys =
    | "smoothiesMade"
    | "sanitizerPassed"
    | "statusCheckPassed"
    | "waterCalibrated"
    | "isPolyfuses";

  type BooleanField = {
    label: string;
    field: BooleanFieldKeys;
    tooltip: string;
    howTo?: { url: string; label?: string };
  };

  const booleanFields: BooleanField[] = [
    {
      label: "Machine has Polyfuses",
      field: "isPolyfuses",
      tooltip: "Indicate if polyfuses are installed in this machine",
    },
    {
      label: "Water level calibrated with scale",
      field: "waterCalibrated",
      tooltip: "Confirm water calibration was completed using a scale",
    },
    {
      label: "Passed sanitizer test",
      field: "sanitizerPassed",
      tooltip: "Confirm the machine passed the sanitizer testing procedure",
      howTo: {
        url: "sanitizer_test",
        label: "How to?",
      },
    },
    {
      label: 'Passed "Get Status" check',
      field: "statusCheckPassed",
      tooltip:
        "Confirm the machine's status check passed and upload proof image",
      howTo: {
        url: "get_status",
        label: "How to?",
      },
    },
    {
      label: "Made 4 smoothies",
      field: "smoothiesMade",
      tooltip:
        "Confirm that 4 test smoothies were successfully made during installation",
    },
  ];

  return (
    <div>
      <header className="header">
        <h2>
          Machine Data (Machine {currentIndex + 1} of{" "}
          {machineDataEntries.length})
        </h2>
      </header>

      {/* <div className="mb-4">
        <div className="left-icon mb-4 mt-4 ms-2" onClick={handleBack}>
          <i className="fa fa-arrow-left" aria-hidden="true"></i>
        </div>
      </div> */}

      <div className="job-wrapper">
        <div className="mb-4 p-4 mt-4 card shadow-sm">
          <div className="mb-3 d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                saveCurrentMachine();
                const stored = JSON.parse(
                  localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
                );
                stored.currentMachineIndex = currentIndex;
                stored.returningFromTicket = true;
                localStorage.setItem(
                  INSTALLATION_JOB_STORAGE_KEY,
                  JSON.stringify(stored),
                );
                setSelectedMachineForTicket(machine);
                setCurrentStep(InstalltionFormSteps.CREATE_TICKET);
              }}
            >
              Create Ticket for Machine {currentIndex + 1}
            </button>
          </div>
          <p>
            <strong>Machine Name:</strong>{" "}
            {storedData?.machineSetup[
              currentIndex
            ]?.newMachineName.toLowerCase() ||
              machine?.machine_name.toLowerCase() ||
              "N/A"}
          </p>
          <p>
            <strong>Serial Number:</strong>{" "}
            {storedData?.machineSetup[currentIndex]?.serialNumber ||
              machine?.serial_number ||
              machine?.serialNumber ||
              "N/A"}
          </p>

          {/* Blender dropdown */}
          <div className="mb-3">
            <label className="fw-bold required machine-data-blender-label mb-0">
              Blender:
            </label>
            <Tooltip
              anchorSelect=".machine-data-blender-label"
              data-placement="top"
            >
              Select the blender model installed in this machine
            </Tooltip>
            <div className="d-flex align-items-center">
              <Select
                className={clsx("react-select", "flex-grow-1")}
                classNamePrefix="my-react-select"
                name="blenderId"
                placeholder="Select Blender"
                options={blenderOptions}
                value={
                  machineDataEntries[currentIndex]?.blenderId
                    ? blenderOptions.find(
                        (opt) =>
                          opt.value ===
                          machineDataEntries[currentIndex]?.blenderId,
                      )
                    : null
                }
                onChange={(option: any) => {
                  const updated = [...machineDataEntries];
                  updated[currentIndex].blenderId = option.value;
                  setMachineDataEntries(updated);
                }}
                formatOptionLabel={(
                  { label, description, media_url }: any,
                  { context }: any,
                ) => {
                  return context === "menu" ? (
                    <div>
                      {label}
                      <div className="text-muted">{description}</div>
                      {media_url && (
                        <div className="mt-2">
                          <img
                            src={media_url}
                            alt={label}
                            style={{ height: "100px", objectFit: "cover" }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    label
                  );
                }}
              />
              <button
                type="button"
                className="btn btn-secondary ms-2"
                title="Refresh"
                onClick={refetchBlenders}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
            </div>
            {validationErrors.blenderId && (
              <small className="text-danger">
                {validationErrors.blenderId}
              </small>
            )}
          </div>

          {/* Board dropdown */}
          {/* <div className="mb-3">
            <label className="fw-bold required machine-data-board-label mb-0">
              Board:
            </label>
            <Tooltip
              anchorSelect=".machine-data-board-label"
              data-placement="top"
            >
              Select the control board installed in this machine
            </Tooltip>
            <div className="d-flex align-items-center">
              <Select
                className={clsx("react-select", "flex-grow-1")}
                classNamePrefix="my-react-select"
                name="boardId"
                placeholder="Select Board"
                options={boardOptions}
                value={
                  machineDataEntries[currentIndex]?.boardId
                    ? boardOptions.find(
                        (opt) =>
                          opt.value ===
                          machineDataEntries[currentIndex]?.boardId,
                      )
                    : null
                }
                onChange={(option: any) => {
                  const updated = [...machineDataEntries];
                  updated[currentIndex].boardId = option.value;
                  setMachineDataEntries(updated);
                }}
                formatOptionLabel={(
                  { label, description, media_url }: any,
                  { context }: any,
                ) => {
                  return context === "menu" ? (
                    <div>
                      {label}
                      <div className="text-muted">{description}</div>
                      {media_url && (
                        <div className="mt-2">
                          <img
                            src={media_url}
                            alt={label}
                            style={{ height: "100px", objectFit: "cover" }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    label
                  );
                }}
              />
              <button
                type="button"
                className="btn btn-secondary ms-2"
                title="Refresh"
                onClick={refetchBoards}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
            </div>
            {validationErrors.boardId && (
              <small className="text-danger">{validationErrors.boardId}</small>
            )}
          </div>

          <div className="mb-3">
            <label className="fw-bold required machine-data-ledring-label mb-0">
              Led Ring:
            </label>
            <Tooltip
              anchorSelect=".machine-data-ledring-label"
              data-placement="top"
            >
              Select the LED ring model installed in this machine
            </Tooltip>
            <div className="d-flex align-items-center">
              <Select
                className={clsx("react-select", "flex-grow-1")}
                classNamePrefix="my-react-select"
                name="ledRingId"
                placeholder="Select Led Ring"
                options={ledRingOptions}
                value={
                  machineDataEntries[currentIndex]?.ledRingId
                    ? ledRingOptions.find(
                        (opt) =>
                          opt.value ===
                          machineDataEntries[currentIndex]?.ledRingId,
                      )
                    : null
                }
                onChange={(option: any) => {
                  const updated = [...machineDataEntries];
                  updated[currentIndex].ledRingId = option.value;
                  setMachineDataEntries(updated);
                }}
                formatOptionLabel={(
                  { label, description, media_url }: any,
                  { context }: any,
                ) => {
                  return context === "menu" ? (
                    <div>
                      {label}
                      <div className="text-muted">{description}</div>
                      {media_url && (
                        <div className="mt-2">
                          <img
                            src={media_url}
                            alt={label}
                            style={{ height: "100px", objectFit: "cover" }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    label
                  );
                }}
              />
              <button
                type="button"
                className="btn btn-secondary ms-2"
                title="Refresh"
                onClick={refetchLedRings}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
            </div>
            {validationErrors.ledRingId && (
              <small className="text-danger">
                {validationErrors.ledRingId}
              </small>
            )}
          </div> */}

          {/* Tube Door Shield dropdown */}
          <div className="mb-3">
            <label className="fw-bold required machine-data-tubeshield-label mb-0">
              Tube Door Shield:
            </label>
            <Tooltip
              anchorSelect=".machine-data-tubeshield-label"
              data-placement="top"
            >
              Select the tube door shield type installed
            </Tooltip>
            <div className="d-flex align-items-center">
              <Select
                className={clsx("react-select", "flex-grow-1")}
                classNamePrefix="my-react-select"
                name="tubeShieldId"
                placeholder="Select Tube Door Shield"
                options={tubeShieldOptions}
                value={
                  machineDataEntries[currentIndex]?.tubeShieldId
                    ? tubeShieldOptions.find(
                        (opt) =>
                          opt.value ===
                          machineDataEntries[currentIndex]?.tubeShieldId,
                      )
                    : null
                }
                onChange={(option: any) => {
                  const updated = [...machineDataEntries];
                  updated[currentIndex].tubeShieldId = option.value;
                  setMachineDataEntries(updated);
                }}
                formatOptionLabel={(
                  { label, description, media_url }: any,
                  { context }: any,
                ) => {
                  return context === "menu" ? (
                    <div>
                      {label}
                      <div className="text-muted">{description}</div>
                      {media_url && (
                        <div className="mt-2">
                          <img
                            src={media_url}
                            alt={label}
                            style={{ height: "100px", objectFit: "cover" }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    label
                  );
                }}
              />
              <button
                type="button"
                className="btn btn-secondary ms-2"
                title="Refresh"
                onClick={refetchTubeShields}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
            </div>
            {validationErrors.tubeShieldId && (
              <small className="text-danger">
                {validationErrors.tubeShieldId}
              </small>
            )}
          </div>
          {/* Cup Holder dropdown */}
          <div className="mb-3">
            <label className="fw-bold required machine-data-cupholder-label mb-0">
              Cup Holder:
            </label>
            <Tooltip
              anchorSelect=".machine-data-cupholder-label"
              data-placement="top"
            >
              Select the cup holder type installed
            </Tooltip>
            <div className="d-flex align-items-center">
              <Select
                className={clsx("react-select", "flex-grow-1")}
                classNamePrefix="my-react-select"
                name="cupHolderId"
                placeholder="Select Cup Holder"
                options={cupHolderOptions}
                value={
                  machineDataEntries[currentIndex]?.cupHolderId
                    ? cupHolderOptions.find(
                        (opt) =>
                          opt.value ===
                          machineDataEntries[currentIndex]?.cupHolderId,
                      )
                    : null
                }
                onChange={(option: any) => {
                  const updated = [...machineDataEntries];
                  updated[currentIndex].cupHolderId = option.value;
                  setMachineDataEntries(updated);
                }}
                formatOptionLabel={(
                  { label, description, media_url }: any,
                  { context }: any,
                ) => {
                  return context === "menu" ? (
                    <div>
                      {label}
                      <div className="text-muted">{description}</div>
                      {media_url && (
                        <div className="mt-2">
                          <img
                            src={media_url}
                            alt={label}
                            style={{ height: "100px", objectFit: "cover" }}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    label
                  );
                }}
              />
              <button
                type="button"
                className="btn btn-secondary ms-2"
                title="Refresh"
                onClick={refetchCupHolders}
              >
                <i className="fa fa-refresh" aria-hidden="true"></i>
              </button>
            </div>
            {validationErrors.cupHolderId && (
              <small className="text-danger">
                {validationErrors.cupHolderId}
              </small>
            )}
          </div>

          <div className="mb-3">
            <label className="fw-bold machine-data-filtertype-label">
              Filter Type:
            </label>
            <Tooltip
              anchorSelect=".machine-data-filtertype-label"
              data-placement="top"
            >
              Select the water filter type: no filter, Smoodi-provided filter,
              or store's filter
            </Tooltip>
            <Select
              className={clsx("react-select")}
              classNamePrefix="my-react-select"
              name="filterType"
              placeholder="Select Filter Type"
              options={filterOptions}
              value={
                machineDataEntries[currentIndex]?.filterType
                  ? filterOptions.find(
                      (opt) =>
                        opt.value ===
                        machineDataEntries[currentIndex]?.filterType,
                    )
                  : null
              }
              onChange={(option: any) => {
                const updated = [...machineDataEntries];
                updated[currentIndex].filterType = option.value;
                setMachineDataEntries(updated);
              }}
            />
          </div>

          {booleanFields.map((item) => (
            <div key={item.field} className="d-flex flex-column mb-2">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <label className={`machine-data-${item.field}-label mb-0`}>
                    {item.label}
                  </label>
                  <Tooltip
                    anchorSelect={`.machine-data-${item.field}-label`}
                    data-placement="top"
                  >
                    {item.tooltip}
                  </Tooltip>
                  {item.howTo?.url && (
                    <a
                      onClick={(e) => getSignedUrlAndOpenIt(item.howTo!.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ marginLeft: 4, cursor: "pointer" }}
                      title={item.howTo.label || "How to"}
                      className="text-primary"
                    >
                      {item.howTo.label || "How to"}{" "}
                      <i
                        className="fa fa-external-link text-primary"
                        aria-hidden="true"
                      ></i>
                    </a>
                  )}
                </div>

                <div className="form-check form-switch form-check-custom">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={entry[item.field]}
                    onChange={() => {
                      const updated = [...machineDataEntries];
                      updated[currentIndex][item.field] =
                        !updated[currentIndex][item.field];

                      if (
                        item.field === "statusCheckPassed" &&
                        !updated[currentIndex][item.field]
                      ) {
                        updated[currentIndex].statusCheckImage = [];
                        setSelectedImages([]);
                        setResultImages([]);
                        setValidationErrors({});
                      }

                      setMachineDataEntries(updated);
                    }}
                  />
                </div>
              </div>

              {item.field === "statusCheckPassed" && entry.statusCheckPassed && (
                <div className="mt-4">
                  <label>Upload get status result image</label>
                  <UploadBox
                    files={selectedImages}
                    onFilesChange={(next) => {
                      setSelectedImages(next);
                      setResultImages(next);
                      setValidationErrors({ statusCheckImage: "" });
                    }}
                    enableCamera={true}
                    accept=".png, .heic, .heif, .jpg, .jpeg, .webp, .mp4, .mkv, .mov, .x-m4v, .video/*"
                  />
                  {validationErrors.statusCheckImage && (
                    <small className="text-danger">
                      {validationErrors.statusCheckImage}
                    </small>
                  )}
                </div>
              )}
            </div>
          ))}

          <div className="mb-3">
            <label className="fw-bold machine-data-notes-label">
              Machine Notes:
            </label>
            <Tooltip
              anchorSelect=".machine-data-notes-label"
              data-placement="top"
            >
              Add any additional notes or observations about this machine
            </Tooltip>
            <textarea
              className="form-control"
              value={entry.notes}
              onChange={(e) => {
                const updated = [...machineDataEntries];
                updated[currentIndex].notes = e.target.value;
                setMachineDataEntries(updated);
              }}
            />
          </div>
        </div>

        {/* <div className="d-flex justify-content-end mt-3">
          

          

          <button
              type="button"
              className="btn btn-primary me-2"
              onClick={handleNext}
            >
              {currentIndex < machineDataEntries.length - 1 ? `Next → Machine ${currentIndex + 2}` : 'Next →'} 
            </button>
        </div> */}
      </div>
      <SimpleMobileStepper
        totalSteps={installationJobStepsRequired.length}
        handleNext={handleNext}
        handleBack={handleBack}
        activeStep={installationJobStepsRequired.findIndex(
          (s) => s.id === InstalltionFormSteps.MACHINE_DATA,
        )}
      />
    </div>
  );
};
